import { Instance, SnapshotOut, types } from 'mobx-state-tree';

import {
  TDeleteUserDeptRoleResult,
  TGetAccessibleDeptsResult,
  TGetUserDeptRolesResult,
  TPostUserDeptRoleResult,
  TUpdateUserDeptRoleResult,
  UserDeptRoleApi,
} from '../../services';
import { Dept, IDept } from '../dept/Dept';
import { withEnvironment } from '../extensions/with-environment';
import { withRootStore } from '../extensions/with-root-store';
import { IUserDeptRole, IUserDeptRoleSnapshot, UserDeptRole } from '../user-dept-role/UserDeptRole';

/**
 * # UserDeptRoleStore
 *
 * 사용자 다중 부서 역할 (겸직) 관리 스토어
 */
export const UserDeptRoleStore = types
  .model('UserDeptRoleStore')
  // --------------------------------------------------------------------------
  .props({
    userDeptRoles: types.optional(types.array(UserDeptRole), []),
    accessibleDepts: types.optional(types.array(Dept), []),
  })
  .extend(withRootStore)
  .extend(withEnvironment)
  // eslint-disable-line @typescript-eslint/no-unused-vars
  .views((self) => ({}))
  // --------------------------------------------------------------------------
  // MUTATEs - 모델 상태를 변경
  .actions((self) => ({
    setUserDeptRoles: (userDeptRoles: IUserDeptRole[]) => self.userDeptRoles.replace(userDeptRoles),
    setAccessibleDepts: (depts: IDept[]) => self.accessibleDepts.replace(depts),
  }))
  // --------------------------------------------------------------------------
  // REQUESTs - 서비스 요청 및 기타 인터페이스 요청
  .actions((self) => ({
    gets: async (userSid: number) => {
      const api: UserDeptRoleApi = new UserDeptRoleApi(self.environment.api);
      const result: TGetUserDeptRolesResult = await api.gets(userSid);
      self.rootStore.responseStore.getResponseResult(result);
      if (result.kind === 'ok') {
        self.setUserDeptRoles(result.userDeptRoles);
      } else {
        console.error(result.kind);
      }
    },
    post: async (userSid: number, data: IUserDeptRoleSnapshot) => {
      const api: UserDeptRoleApi = new UserDeptRoleApi(self.environment.api);
      const result: TPostUserDeptRoleResult = await api.post(userSid, data);
      self.rootStore.responseStore.getResponseResult(result);
      if (result.kind === 'ok') {
        return;
      } else {
        console.error(result.kind);
      }
    },
    update: async (userSid: number, data: IUserDeptRoleSnapshot) => {
      const api: UserDeptRoleApi = new UserDeptRoleApi(self.environment.api);
      const result: TUpdateUserDeptRoleResult = await api.update(userSid, data);
      self.rootStore.responseStore.getResponseResult(result);
      if (result.kind === 'ok') {
        return;
      } else {
        console.error(result.kind);
      }
    },
    delete: async (userSid: number, userDeptRoleSid: number) => {
      const api: UserDeptRoleApi = new UserDeptRoleApi(self.environment.api);
      const result: TDeleteUserDeptRoleResult = await api.delete(userSid, userDeptRoleSid);
      self.rootStore.responseStore.getResponseResult(result);
      if (result.kind === 'ok') {
        return;
      } else {
        console.error(result.kind);
      }
    },
    getAccessibleDepts: async () => {
      const api: UserDeptRoleApi = new UserDeptRoleApi(self.environment.api);
      const result: TGetAccessibleDeptsResult = await api.getAccessibleDepts();
      self.rootStore.responseStore.getResponseResult(result);
      if (result.kind === 'ok') {
        self.setAccessibleDepts(result.accessibleDepts);
      } else {
        console.error(result.kind);
      }
    },
  }));

// --------------------------------------------------------------------------
type TUserDeptRoleStore = Instance<typeof UserDeptRoleStore>;
type TUserDeptRoleStoreSnapshot = SnapshotOut<typeof UserDeptRoleStore>;

export interface IUserDeptRoleStore extends TUserDeptRoleStore {}
export type TUserDeptRoleStoreKeys = keyof TUserDeptRoleStoreSnapshot & string;
export interface IUserDeptRoleStoreSnapshot extends TUserDeptRoleStoreSnapshot {}
export const createUserDeptRoleStore = () =>
  types.optional(UserDeptRoleStore, {} as TUserDeptRoleStore);
